import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { markChunkLoaded } from './utils/tabPerf';
import TabPerfTracker from './utils/TabPerfTracker';

// Wrap a lazy() factory so the chunk's first-load time is recorded against
// the route path. Used for the main navbar destinations only — adding it
// to a hundred non-tab lazy chunks would just be noise.
const lazyTracked = (path, factory) =>
  lazy(() =>
    factory().then((m) => {
      markChunkLoaded(path);
      return m;
    }),
  );
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import PushPermissionBanner from './components/notifications/PushPermissionBanner';
import TestProfileSwitcher from './components/dev/TestProfileSwitcher';

import { Toaster } from './components/ui/toaster';
import { startKeepAlive } from './utils/keepAlive';

// Context Providers
import { AuthProvider, useAuth } from './context/AuthContext';
import { YouTubeProvider } from './context/YouTubeContext';
import { EventsProvider } from './context/EventsContext';

import { ThemeProvider } from './context/ThemeContext';
import { AppModeProvider } from './context/AppModeContext';
import { DataCacheProvider } from './context/DataCacheContext';

// Route Protection
import { ProtectedRoute, MemberRoute, LeaderRoute, AuthenticatedRoute } from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { VisitorRoute } from './components/VisitorRoute';

// Auth Pages (eagerly loaded — needed immediately)
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ActivateAccount from './pages/ActivateAccount';
import PublicDeleteRequest from './pages/PublicDeleteRequest';
import Screen_DeleteAccount from './pages/Screen_DeleteAccount';
import Unauthorized from './pages/Unauthorized';

// Visitor Pages — HomeScreen eager (first paint), rest lazy
import HomeScreen from './pages/HomeScreen';
const WatchScreen = lazy(() => import('./pages/WatchScreen'));
const EventsScreen = lazy(() => import('./pages/EventsScreen'));
const EventDetailScreen = lazy(() => import('./pages/EventDetailScreen'));
const GivingScreen = lazy(() => import('./pages/GivingScreen'));
const LibraryScreen = lazy(() => import('./pages/LibraryScreen'));
const ContactScreen = lazy(() => import('./pages/ContactScreen'));
const PrayerLineScreen = lazy(() => import('./pages/PrayerLineScreen'));
const VisitorAppointmentScreen = lazy(() => import('./pages/VisitorAppointmentScreen'));
const Screen_VisitorAppointmentRequest = lazy(() => import('./pages/Screen_VisitorAppointmentRequest'));
const VisitorProfileScreen = lazy(() => import('./pages/VisitorProfileScreen'));
// Public CMS page renderer (Sprint 3) — renders published Puck pages.
const PuckPageRenderer = lazy(() => import('./pages/PuckPageRenderer'));
const CmsHomePreview = lazy(() => import('./pages/CmsHomePreview')); // aperçu accueil pour le CMS (intranet)

// Member Pages — all lazy loaded (only needed for authenticated members)
const MemberNotes = lazy(() => import('./pages/member/MemberNotes'));
const MemberEvents = lazy(() => import('./pages/member/MemberEvents'));
const MemberProfile = lazy(() => import('./pages/member/MemberProfile'));
const MemberRequests = lazy(() => import('./pages/member/MemberRequests'));
const Screen_MemberHome = lazyTracked('/member/home', () => import('./pages/member/Screen_MemberHome'));
const Screen_MemberCalendar = lazyTracked('/member/calendar', () => import('./pages/member/Screen_MemberCalendar'));
const Screen_MemberSettings = lazyTracked('/member/settings', () => import('./pages/member/Screen_MemberSettings'));
const Screen_MemberPlaceholder = lazy(() => import('./pages/member/Screen_MemberPlaceholder'));
const Screen_MemberAppointmentRequest = lazy(() => import('./pages/member/Screen_MemberAppointmentRequest'));
const Screen_MemberAppointments = lazy(() => import('./pages/member/Screen_MemberAppointments'));
const Screen_MemberMinistryTeam = lazy(() => import('./pages/member/Screen_MemberMinistryTeam'));
const Screen_MemberParcours = lazy(() => import('./pages/member/Screen_MemberParcours'));
const Screen_MemberAbsence = lazy(() => import('./pages/member/Screen_MemberAbsence'));
const Screen_WebViewParcours = lazy(() => import('./pages/member/Screen_WebViewParcours'));
const Screen_MemberCarte = lazyTracked('/member/carte', () => import('./pages/member/Screen_MemberCarte'));
const Screen_MemberAttendance = lazy(() => import('./pages/member/Screen_MemberAttendance'));
const Screen_MemberMissions = lazy(() => import('./pages/member/Screen_MemberMissions'));
const Screen_MemberMissionDetail = lazy(() => import('./pages/member/Screen_MemberMissionDetail'));
const Screen_MemberEvangelisation = lazy(() => import('./pages/member/Screen_MemberEvangelisation'));
const Screen_MemberLeaders = lazy(() => import('./pages/member/Screen_MemberLeaders'));
const Screen_MemberLeaderDetail = lazy(() => import('./pages/member/Screen_MemberLeaderDetail'));
const Screen_MemberMission = lazy(() => import('./pages/member/Screen_MemberMission'));
const Screen_MemberEvangelisationPlaceholder = lazy(() => import('./pages/member/Screen_MemberEvangelisationPlaceholder'));
const Screen_Notifications = lazy(() => import('./pages/notifications/Screen_Notifications'));

// Scanner Pages — lazy loaded (role-specific)
const Screen_ScannerHome = lazy(() => import('./pages/scanner/Screen_ScannerHome'));
const Screen_ScannerScan = lazy(() => import('./pages/scanner/Screen_ScannerScan'));
const Screen_ScannerSettings = lazy(() => import('./pages/scanner/Screen_ScannerSettings'));

// Leader Pages — lazy loaded (role-specific)
const LeaderDashboard = lazy(() => import('./pages/leader/LeaderDashboard'));
const LeaderGDM = lazy(() => import('./pages/leader/LeaderGDM'));
const LeaderAnalytics = lazy(() => import('./pages/leader/LeaderAnalytics'));
const LeaderContacts = lazy(() => import('./pages/leader/LeaderContacts'));
const LeaderActivities = lazy(() => import('./pages/leader/LeaderActivities'));
const LeaderDepartement = lazy(() => import('./pages/leader/LeaderDepartement'));
const LeaderPlanning = lazy(() => import('./pages/leader/LeaderPlanning'));
const LeaderPlanningsOverview = lazy(() => import('./pages/leader/LeaderPlanningsOverview'));
const LeaderReconcile = lazy(() => import('./pages/leader/LeaderReconcile'));
const LeaderMembers = lazy(() => import('./pages/leader/LeaderMembers'));
const ReconcilerDashboard = lazy(() => import('./pages/leader/ReconcilerDashboard'));
const LeaderTasks = lazy(() => import('./pages/leader/LeaderTasks'));
const LeaderContactDetail = lazy(() => import('./pages/leader/LeaderContactDetail'));
const LeaderRdvSession = lazy(() => import('./pages/leader/LeaderRdvSession'));
const LeaderCommunication = lazy(() => import('./pages/leader/LeaderCommunication'));
const LeaderEvents = lazy(() => import('./pages/leader/LeaderEvents'));
const Screen_LeaderSettings = lazy(() => import('./pages/leader/Screen_LeaderSettings'));
const LeaderAvailability = lazy(() => import('./pages/leader/LeaderAvailability'));
const Screen_LeaderRDV = lazyTracked('/leader/rdv', () => import('./pages/leader/Screen_LeaderRDV'));
const Screen_LeaderFollowUpHistory = lazy(() => import('./pages/leader/Screen_LeaderFollowUpHistory'));
const Screen_MemberExercises = lazy(() => import('./pages/member/Screen_MemberExercises'));
const Screen_MemberParcoursPastoral = lazy(() => import('./pages/member/Screen_MemberParcoursPastoral'));
const Screen_LeaderMissions = lazy(() => import('./pages/leader/Screen_LeaderMissions'));
const Screen_LeaderAlerts = lazy(() => import('./pages/leader/Screen_LeaderAlerts'));
const Screen_PastoralAlerts = lazy(() => import('./pages/alerts/Screen_PastoralAlerts'));
const Screen_LeaderAlertDetail = lazy(() => import('./pages/leader/Screen_LeaderAlertDetail'));
const Screen_LeaderAlertCreate = lazy(() => import('./pages/leader/Screen_LeaderAlertCreate'));

// Utilisateur Pages — lazy loaded (role-specific)
// Ancienne fiche responsable retirée (consolidation 2026-09-14). La route
// /utilisateur/contacts/:id redirige vers la fiche refonte /leader/contacts/:id
// en conservant les query params (?tab=, ?openFollowUp=, ?openAppointment=),
// désormais tous gérés par LeaderContactDetail (parité complète).
function RedirectToLeaderContact() {
  const { contactId } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/leader/contacts/${contactId}${search}`} replace />;
}

// Shared Pages — lazy loaded
const Screen_VideoSession = lazy(() => import('./pages/shared/Screen_VideoSession'));

// 404 Page — lazy loaded
const NotFoundScreen = lazy(() => import('./pages/NotFoundScreen'));

// DG Pages — lazy loaded (role-specific)
import DGRoute from './components/routes/DGRoute';
import YouthRoute from './components/routes/YouthRoute';
import ConseillerRoute from './components/routes/ConseillerRoute';
const Screen_DGDashboard = lazyTracked('/dg/dashboard', () => import('./pages/dg/Screen_DGDashboard'));
const Screen_DGSectors = lazy(() => import('./pages/dg/Screen_DGSectors'));
const Screen_DGLeaders = lazy(() => import('./pages/dg/Screen_DGLeaders'));
const Screen_DGMembers = lazy(() => import('./pages/dg/Screen_DGMembers'));
const Screen_DGSettings = lazyTracked('/dg/settings', () => import('./pages/dg/Screen_DGSettings'));
const Screen_DGOrganisation = lazy(() => import('./pages/dg/Screen_DGOrganisation'));
const Screen_DGDepartementDetail = lazy(() => import('./pages/dg/Screen_DGDepartementDetail'));
const Screen_DGMissions = lazy(() => import('./pages/dg/Screen_DGMissions'));
// Alertes & signalements fusionnés (consolidation 2026-10).
const Screen_DGAlertsHub = lazyTracked('/dg/alerts', () => import('./pages/dg/Screen_DGAlertsHub'));

// Direction Jeunesse sub-space — gated server-side via youthRoleAccess
const Screen_YouthDashboard = lazy(() => import('./pages/youth/Screen_YouthDashboard'));
const Screen_YouthGroup = lazy(() => import('./pages/youth/Screen_YouthGroup'));
const Screen_YouthMemberDetail = lazy(() => import('./pages/youth/Screen_YouthMemberDetail'));

// Espace Conseillers — suivi des nouveaux convertis. Gardé serveur via la
// capacité conseillerAccess / conseillerLead (flag Airtable additif).
const ConseillerDashboard = lazy(() => import('./pages/conseiller/ConseillerDashboard'));
const ConseillerNewConvert = lazy(() => import('./pages/conseiller/ConseillerNewConvert'));
const ConseillerCaseDetail = lazy(() => import('./pages/conseiller/ConseillerCaseDetail'));
const ConseillerDepartment = lazy(() => import('./pages/conseiller/ConseillerDepartment'));

// Scroll to top on every route change — EXCEPT when the transition involves
// an overlay route (state.backgroundLocation). Opening/closing an overlay
// must preserve the background screen's scroll position so the user returns
// exactly where they left off.
const ScrollToTop = () => {
  const location = useLocation();
  const prevLocation = useRef(location);
  useEffect(() => {
    const isOverlay = !!location.state?.backgroundLocation;
    const wasOverlay = !!prevLocation.current.state?.backgroundLocation;
    if (!isOverlay && !wasOverlay) {
      window.scrollTo(0, 0);
    }
    prevLocation.current = location;
  }, [location]);
  return null;
};

// TestAccountBanner was removed — it was breaking layout in the Direction
// space and the demo accounts don't need a persistent badge on screen.
const AuthenticatedOverlay = () => {
  // Renders global overlays that only matter once the user is authenticated.
  // Today: the one-time push-notification permission banner for leaders + DG.
  // The banner self-suppresses for members/visitors and when already
  // dismissed/granted.
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return null;
  return (
    <>
      <PushPermissionBanner />
      <TestProfileSwitcher />
    </>
  );
};

// Bridges service-worker notificationclick → React Router navigation.
// When the SW posts { type: 'navigate', url } we route via React Router
// instead of a full reload — keeps the SPA state intact.
const SWNavigationBridge = () => {
  const navigate = useNavigate();
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMessage = (event) => {
      if (event.data?.type === 'navigate' && typeof event.data.url === 'string') {
        try {
          const url = new URL(event.data.url, window.location.origin);
          if (url.origin === window.location.origin) {
            navigate(url.pathname + url.search + url.hash);
          }
        } catch (_) { /* ignore */ }
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);
  return null;
};

// Onboarding modal was removed — kept showing across sessions despite multiple
// fixes to the per-user storage key. Per team decision: no tutorial at all is
// better than one that annoys returning users on every login.

// Loading fallback for lazy-loaded routes.
// Two-stage: for 220ms nothing renders — the current page stays visible so
// preloaded chunks feel instant. After 220ms the lion splash appears (for
// the rare cold load). Result: no white flash on tab-hover-preloaded nav.
const LazyFallback = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 220);
    return () => clearTimeout(t);
  }, []);
  // On peint TOUJOURS un fond thémé immédiatement (jamais `return null`) : sinon
  // pendant les ~220ms de chargement d'un chunk, l'ancienne page est démontée et
  // rien ne peint le fond → flash d'écran noir (surtout en sombre). Le
  // logo + spinner n'apparaissent qu'au-delà de 220ms (pas de flash sur un
  // chargement rapide), mais le fond, lui, est là dès la première frame.
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-pcc-bg">
      {visible && (
        <div className="flex flex-col items-center animate-fade-in-fast">
          <img src="/logo-lion-light.png" alt="PCC" className="w-20 h-20 mb-4 hidden dark:block" />
          <img src="/logo-lion-dark.png" alt="PCC" className="w-20 h-20 mb-4 dark:hidden" />
          <div className="w-6 h-6 border-3 border-pcc-card-border border-t-pcc-text-muted rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

// Prefetch critical member chunks after login so tab switches are instant
const prefetchMemberChunks = () => {
  const chunks = [
    () => import('./pages/member/Screen_MemberHome'),
    () => import('./pages/member/Screen_MemberCalendar'),
    () => import('./pages/member/Screen_MemberSettings'),
    () => import('./pages/member/Screen_MemberCarte'),
    () => import('./pages/member/Screen_MemberAppointments'),
    () => import('./pages/member/Screen_MemberMissions'),
    () => import('./pages/member/MemberProfile'),
    () => import('./pages/member/Screen_MemberParcours'),
  ];
  // Stagger imports to avoid blocking main thread
  chunks.forEach((load, i) => setTimeout(load, i * 100));
};

// Prefetch on idle after auth is confirmed
const MemberPrefetcher = () => {
  const { isAuthenticated } = useAuth();
  useEffect(() => {
    if (isAuthenticated) {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(prefetchMemberChunks);
      } else {
        setTimeout(prefetchMemberChunks, 1000);
      }
    }
  }, [isAuthenticated]);
  return null;
};

// Start keep-alive ping to prevent Railway cold starts
startKeepAlive();

// Scanner route wrapper - Accessible to users with can_scan permission or admin/dg roles
const ScannerRoute = ({ children }) => {
  const { user, isAuthenticated, loading, previewMode, isRealAdmin, hasPermission } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-[#272727]">
        <div className="w-10 h-10 border-4 border-[#9398DE]/30 border-t-[#9398DE] rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Allow real admins in preview mode
  if (previewMode && isRealAdmin()) {
    return children;
  }

  // Allow anyone with can_scan permission, or admin/dg roles
  if (hasPermission('can_scan') || ['admin', 'dg'].includes(user?.role)) {
    return children;
  }

  return <Navigate to="/" replace />;
};

// AppRoutes is rendered inside BrowserRouter so it can use useLocation.
// It implements the "background location" pattern: when a user clicks an event
// from the home/events screen, the detail URL is pushed with state carrying the
// ORIGINAL location. We render the main <Routes> at that background location
// (so HomeScreen stays mounted) and render a SECOND <Routes> on top for the
// detail overlay. Navigating back pops the history — the background screen is
// already there, no remount, no lag.
function AppRoutes() {
  const location = useLocation();
  const backgroundLocation = location.state?.backgroundLocation;
  // Gate rendering on auth loading. When a user just logged in, React may
  // batch state updates in a way where the Routes briefly render with the
  // PREVIOUS role — causing a leader to see the member space for a flash
  // before bouncing to their correct dashboard. Block rendering until the
  // auth context has finished its initial check + any in-flight login.
  const { loading: authLoading } = useAuth();

  if (authLoading) {
    return <LazyFallback />;
  }

  return (
    <>
      <TabPerfTracker />
      <Suspense fallback={<LazyFallback />}>
        <Routes location={backgroundLocation || location}>
          {/* MAIN_ROUTES_START */}
                  {/* ========================================
                      VISITOR ROUTES (Public - Mobile Only)
                      Desktop visitors redirected to PCC website
                     ======================================== */}
                  <Route path="/" element={
                    <VisitorRoute>
                      <HomeScreen />
                    </VisitorRoute>
                  } />
                  <Route path="/watch" element={
                    <VisitorRoute>
                      <WatchScreen />
                    </VisitorRoute>
                  } />
                  <Route path="/events" element={
                    <VisitorRoute>
                      <EventsScreen />
                    </VisitorRoute>
                  } />
                  <Route path="/events/:eventId" element={
                    <VisitorRoute>
                      <EventDetailScreen />
                    </VisitorRoute>
                  } />
                  <Route path="/giving" element={
                    <VisitorRoute>
                      <GivingScreen />
                    </VisitorRoute>
                  } />
                  <Route path="/library" element={
                    <VisitorRoute>
                      <LibraryScreen />
                    </VisitorRoute>
                  } />
                  <Route path="/contact" element={
                    <VisitorRoute>
                      <ContactScreen />
                    </VisitorRoute>
                  } />
                  <Route path="/prayer" element={
                    <VisitorRoute>
                      <PrayerLineScreen />
                    </VisitorRoute>
                  } />
                  <Route path="/visitor/appointment" element={
                    <VisitorRoute>
                      <VisitorAppointmentScreen />
                    </VisitorRoute>
                  } />
                  <Route path="/visitor/appointment-request" element={
                    <VisitorRoute>
                      <Screen_VisitorAppointmentRequest />
                    </VisitorRoute>
                  } />
                  <Route path="/visitor/profile" element={
                    <VisitorRoute>
                      <VisitorProfileScreen />
                    </VisitorRoute>
                  } />

                {/* Public CMS pages (Sprint 3) — published Puck pages.
                    No auth: church content anyone can open via a shared
                    /page/<slug> link. */}
                <Route path="/page/:slug" element={<PuckPageRenderer />} />

                {/* Aperçu de l'accueil pour le CMS (intranet) — public, embarqué
                    en iframe par l'éditeur Puck, piloté par postMessage. */}
                <Route path="/cms-home-preview" element={<CmsHomePreview />} />

                {/* ========================================
                    AUTH ROUTES (Public)
                   ======================================== */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/activate" element={<ActivateAccount />} />
                {/* Public account-deletion request (Google Play requirement, no auth) */}
                <Route path="/supprimer-mon-compte" element={<PublicDeleteRequest />} />
                {/* In-app account deletion (auth enforced by the screen's authFetch) */}
                <Route path="/compte/supprimer" element={<Screen_DeleteAccount />} />
                <Route path="/unauthorized" element={<Unauthorized />} />

                {/* Notification Center — accessible by all authenticated roles */}
                <Route path="/notifications" element={
                  <MemberRoute>
                    <Screen_Notifications />
                  </MemberRoute>
                } />

                {/* ========================================
                    MEMBER ROUTES (Protected - Mobile + Web)
                    Requires role: member or higher
                   ======================================== */}
                <Route path="/member/home" element={
                  <MemberRoute>
                    <Screen_MemberHome />
                  </MemberRoute>
                } />
                <Route path="/member/calendar" element={
                  <MemberRoute>
                    <Screen_MemberCalendar />
                  </MemberRoute>
                } />
                {/* Chat/messaging routes removed from member space */}
                <Route path="/member/settings" element={
                  <AuthenticatedRoute>
                    <Screen_MemberSettings />
                  </AuthenticatedRoute>
                } />
                <Route path="/member/placeholder" element={
                  <MemberRoute>
                    <Screen_MemberPlaceholder />
                  </MemberRoute>
                } />
                <Route path="/member/dashboard" element={
                  <MemberRoute>
                    <Screen_MemberHome />
                  </MemberRoute>
                } />
                <Route path="/member/notes" element={
                  <MemberRoute>
                    <MemberNotes />
                  </MemberRoute>
                } />
                <Route path="/member/events" element={
                  <MemberRoute>
                    <MemberEvents />
                  </MemberRoute>
                } />
                <Route path="/member/profile" element={
                  <AuthenticatedRoute>
                    <MemberProfile />
                  </AuthenticatedRoute>
                } />
                <Route path="/member/absence" element={
                  <AuthenticatedRoute>
                    <Screen_MemberAbsence />
                  </AuthenticatedRoute>
                } />
                <Route path="/member/requests" element={
                  <MemberRoute>
                    <MemberRequests />
                  </MemberRoute>
                } />
                {/* « Mes groupes » retiré de l'espace membre (décision Louis/Jonathan,
                    2026-09-13) : la structure des groupes est gérée par la direction /
                    Airtable, pas en libre-service côté membre. Les anciennes URLs
                    redirigent vers l'accueil pour ne pas casser un lien/bookmark. */}
                <Route path="/member/groups" element={
                  <Navigate to="/member/home" replace />
                } />
                <Route path="/member/groups/:groupId" element={
                  <Navigate to="/member/home" replace />
                } />
                <Route path="/member/leaders" element={
                  <MemberRoute>
                    <Screen_MemberLeaders />
                  </MemberRoute>
                } />
                <Route path="/member/leaders/:leaderId" element={
                  <MemberRoute>
                    <Screen_MemberLeaderDetail />
                  </MemberRoute>
                } />
                <Route path="/member/mission" element={
                  <MemberRoute>
                    <Screen_MemberMission />
                  </MemberRoute>
                } />
                <Route path="/member/evangelisation" element={
                  <MemberRoute>
                    <Screen_MemberEvangelisationPlaceholder />
                  </MemberRoute>
                } />
                <Route path="/member/appointments" element={
                  <MemberRoute>
                    <Screen_MemberAppointments />
                  </MemberRoute>
                } />
                <Route path="/member/exercises" element={
                  <MemberRoute>
                    <Screen_MemberExercises />
                  </MemberRoute>
                } />
                <Route path="/member/parcours-pastoral" element={
                  <MemberRoute>
                    <Screen_MemberParcoursPastoral />
                  </MemberRoute>
                } />
                <Route path="/member/appointments/new" element={
                  <MemberRoute>
                    <ErrorBoundary fallbackTitle="Erreur lors de la demande de rendez-vous">
                      <Screen_MemberAppointmentRequest />
                    </ErrorBoundary>
                  </MemberRoute>
                } />
                <Route path="/member/ministry/:departementCode" element={
                  <MemberRoute>
                    <Screen_MemberMinistryTeam />
                  </MemberRoute>
                } />
                <Route path="/member/parcours" element={
                  <MemberRoute>
                    <Screen_MemberParcours />
                  </MemberRoute>
                } />
                <Route path="/member/parcours/webview" element={
                  <MemberRoute>
                    <Screen_WebViewParcours />
                  </MemberRoute>
                } />
                <Route path="/member/carte" element={
                  <MemberRoute>
                    <Screen_MemberCarte />
                  </MemberRoute>
                } />
                <Route path="/member/attendance" element={
                  <MemberRoute>
                    <Screen_MemberAttendance />
                  </MemberRoute>
                } />
                <Route path="/member/missions" element={
                  <MemberRoute>
                    <Screen_MemberMissions />
                  </MemberRoute>
                } />
                <Route path="/member/missions/:id" element={
                  <MemberRoute>
                    <Screen_MemberMissionDetail />
                  </MemberRoute>
                } />
                <Route path="/member/missions/:id/evangelisation" element={
                  <MemberRoute>
                    <Screen_MemberEvangelisation />
                  </MemberRoute>
                } />

                {/* ========================================
                    LEADER ROUTES (Protected - Mobile + Web)
                    Requires role: leader or admin
                   ======================================== */}
                <Route path="/leader/dashboard" element={
                  <LeaderRoute>
                    <LeaderDashboard />
                  </LeaderRoute>
                } />
                <Route path="/leader/departement" element={
                  <LeaderRoute>
                    <LeaderDepartement />
                  </LeaderRoute>
                } />
                <Route path="/leader/departement/planning" element={
                  <LeaderRoute>
                    <LeaderPlanning />
                  </LeaderRoute>
                } />
                <Route path="/leader/plannings" element={
                  <LeaderRoute>
                    <LeaderPlanningsOverview />
                  </LeaderRoute>
                } />
                <Route path="/leader/reconcile" element={
                  <LeaderRoute>
                    <LeaderReconcile />
                  </LeaderRoute>
                } />
                <Route path="/leader/membres" element={
                  <LeaderRoute>
                    <LeaderMembers />
                  </LeaderRoute>
                } />
                <Route path="/leader/reconciler" element={
                  <LeaderRoute>
                    <ReconcilerDashboard />
                  </LeaderRoute>
                } />
                <Route path="/leader/gdm" element={
                  <LeaderRoute>
                    <LeaderGDM />
                  </LeaderRoute>
                } />
                <Route path="/leader/analytics" element={
                  <LeaderRoute>
                    <LeaderAnalytics />
                  </LeaderRoute>
                } />
                <Route path="/leader/contacts" element={
                  <LeaderRoute>
                    <LeaderContacts />
                  </LeaderRoute>
                } />
                <Route path="/leader/activities" element={
                  <LeaderRoute>
                    <LeaderActivities />
                  </LeaderRoute>
                } />
                <Route path="/leader/tasks" element={
                  <LeaderRoute>
                    <LeaderTasks />
                  </LeaderRoute>
                } />
                <Route path="/leader/contacts/:contactId" element={
                  <LeaderRoute>
                    <LeaderContactDetail />
                  </LeaderRoute>
                } />
                <Route path="/leader/rdv/session/:appointmentId" element={
                  <LeaderRoute>
                    <LeaderRdvSession />
                  </LeaderRoute>
                } />
                <Route path="/leader/communication" element={
                  <LeaderRoute>
                    <LeaderCommunication />
                  </LeaderRoute>
                } />
                <Route path="/leader/events" element={
                  <LeaderRoute>
                    <LeaderEvents />
                  </LeaderRoute>
                } />
                <Route path="/leader/settings" element={
                  <LeaderRoute>
                    <Screen_LeaderSettings />
                  </LeaderRoute>
                } />
                <Route path="/leader/availability" element={
                  <LeaderRoute>
                    <LeaderAvailability />
                  </LeaderRoute>
                } />
                <Route path="/leader/follow-ups/member/:memberId" element={
                  <LeaderRoute>
                    <Screen_LeaderFollowUpHistory />
                  </LeaderRoute>
                } />
                <Route path="/leader/rdv" element={
                  <LeaderRoute>
                    <ErrorBoundary fallbackTitle="Erreur sur la page Rendez-vous">
                      <Screen_LeaderRDV />
                    </ErrorBoundary>
                  </LeaderRoute>
                } />
                <Route path="/leader/missions" element={
                  <LeaderRoute>
                    <Screen_LeaderMissions />
                  </LeaderRoute>
                } />
                <Route path="/dg/signalements" element={<Navigate to="/dg/alerts" replace />} />
                  <Route path="/alertes-suivi" element={
                  <LeaderRoute>
                    <ErrorBoundary fallbackTitle="Alertes de suivi indisponibles">
                      <Screen_PastoralAlerts />
                    </ErrorBoundary>
                  </LeaderRoute>
                } />
                {/* Alertes pastorales — /new must be BEFORE /:id so the catchall
                    doesn't match the literal "new" segment. */}
                <Route path="/leader/alerts" element={
                  <LeaderRoute>
                    <ErrorBoundary fallbackTitle="Erreur sur la page Alertes">
                      <Screen_LeaderAlerts />
                    </ErrorBoundary>
                  </LeaderRoute>
                } />
                <Route path="/leader/alerts/new" element={
                  <LeaderRoute>
                    <ErrorBoundary fallbackTitle="Erreur lors de la création d'alerte">
                      <Screen_LeaderAlertCreate />
                    </ErrorBoundary>
                  </LeaderRoute>
                } />
                <Route path="/leader/alerts/:id" element={
                  <LeaderRoute>
                    <ErrorBoundary fallbackTitle="Erreur sur l'alerte">
                      <Screen_LeaderAlertDetail />
                    </ErrorBoundary>
                  </LeaderRoute>
                } />

                {/* ========================================
                    UTILISATEUR ROUTES (Leader Area - Mobile)
                    Requires role: leader, staff, or admin
                   ======================================== */}
                {/* Ancien espace /utilisateur retiré (consolidation 2026-09-14) :
                    ces écrans redirigent vers leur équivalent refonte /leader/*. */}
                <Route path="/utilisateur/dashboard" element={<Navigate to="/leader/dashboard" replace />} />
                <Route path="/utilisateur/groups" element={<Navigate to="/leader/gdm" replace />} />
                <Route path="/utilisateur/groups/:groupId" element={<Navigate to="/leader/gdm" replace />} />
                <Route path="/utilisateur/contacts" element={<Navigate to="/leader/contacts" replace />} />
                <Route path="/utilisateur/contacts/:contactId" element={
                  <LeaderRoute>
                    <RedirectToLeaderContact />
                  </LeaderRoute>
                } />
                <Route path="/utilisateur/parcours" element={<Navigate to="/leader/dashboard" replace />} />
                <Route path="/utilisateur/activities" element={<Navigate to="/leader/activities" replace />} />
                <Route path="/utilisateur/actions" element={<Navigate to="/leader/tasks" replace />} />
                <Route path="/utilisateur/calendar" element={<Navigate to="/leader/rdv" replace />} />
                <Route path="/utilisateur/chats" element={<Navigate to="/leader/communication" replace />} />
                <Route path="/utilisateur/settings" element={<Navigate to="/leader/settings" replace />} />

                {/* ========================================
                    LEGACY REDIRECTS
                   ======================================== */}
                <Route path="/dashboard/member" element={<Navigate to="/member/dashboard" replace />} />
                <Route path="/dashboard/leader" element={<Navigate to="/leader/dashboard" replace />} />

                {/* ========================================
                    SHARED ROUTES (Accessible by both Member and Leader)
                   ======================================== */}
                <Route path="/session/:appointmentId" element={
                  <ProtectedRoute>
                    <Screen_VideoSession />
                  </ProtectedRoute>
                } />

                {/* ========================================
                    DG ROUTES (Admin Only - Director General)
                   ======================================== */}
                <Route path="/dg" element={<Navigate to="/dg/dashboard" replace />} />
                <Route path="/dg/dashboard" element={
                  <DGRoute>
                    <Screen_DGDashboard />
                  </DGRoute>
                } />

                {/* Direction Jeunesse sub-space */}
                <Route path="/youth" element={<Navigate to="/youth/dashboard" replace />} />
                <Route path="/youth/dashboard" element={
                  <YouthRoute>
                    <Screen_YouthDashboard />
                  </YouthRoute>
                } />
                <Route path="/youth/group/:groupName" element={
                  <YouthRoute>
                    <Screen_YouthGroup />
                  </YouthRoute>
                } />
                <Route path="/youth/member/:userId" element={
                  <YouthRoute>
                    <Screen_YouthMemberDetail />
                  </YouthRoute>
                } />

                {/* Espace Conseillers — suivi des nouveaux convertis */}
                <Route path="/conseiller" element={<Navigate to="/conseiller/dashboard" replace />} />
                <Route path="/conseiller/dashboard" element={
                  <ConseillerRoute>
                    <ConseillerDashboard />
                  </ConseillerRoute>
                } />
                <Route path="/conseiller/nouveau" element={
                  <ConseillerRoute>
                    <ConseillerNewConvert />
                  </ConseillerRoute>
                } />
                <Route path="/conseiller/cases/:memberId" element={
                  <ConseillerRoute>
                    <ConseillerCaseDetail />
                  </ConseillerRoute>
                } />
                <Route path="/conseiller/departement" element={
                  <ConseillerRoute>
                    <ConseillerDepartment />
                  </ConseillerRoute>
                } />
                <Route path="/dg/sectors" element={
                  <DGRoute>
                    <Screen_DGSectors />
                  </DGRoute>
                } />
                <Route path="/dg/sectors/:sectorCode" element={
                  <DGRoute>
                    <Screen_DGSectors />
                  </DGRoute>
                } />
                <Route path="/dg/leaders" element={
                  <DGRoute>
                    <Screen_DGLeaders />
                  </DGRoute>
                } />
                <Route path="/dg/leaders/:leaderId" element={
                  <DGRoute>
                    <Screen_DGLeaders />
                  </DGRoute>
                } />
                <Route path="/dg/members" element={
                  <DGRoute>
                    <Screen_DGMembers />
                  </DGRoute>
                } />
                <Route path="/dg/members/:memberId" element={
                  <DGRoute>
                    <Screen_DGMembers />
                  </DGRoute>
                } />
                {/* Rapports & Statistiques fusionnés dans la Vue globale (2026-10). */}
                <Route path="/dg/reports" element={<Navigate to="/dg/dashboard" replace />} />
                <Route path="/dg/stats" element={<Navigate to="/dg/dashboard" replace />} />
                <Route path="/dg/settings" element={
                  <DGRoute>
                    <Screen_DGSettings />
                  </DGRoute>
                } />
                <Route path="/dg/organisation" element={
                  <DGRoute>
                    <Screen_DGOrganisation />
                  </DGRoute>
                } />
                <Route path="/dg/organisation/:departementCode" element={
                  <DGRoute>
                    <Screen_DGDepartementDetail />
                  </DGRoute>
                } />
                <Route path="/dg/missions" element={
                  <DGRoute>
                    <Screen_DGMissions />
                  </DGRoute>
                } />
                <Route path="/dg/alerts" element={
                  <DGRoute>
                    <ErrorBoundary fallbackTitle="Erreur sur les alertes">
                      <Screen_DGAlertsHub />
                    </ErrorBoundary>
                  </DGRoute>
                } />
                {/* « Équipe » fusionné dans « Responsables » (doublon) — on redirige. */}
                <Route path="/dg/team" element={<Navigate to="/dg/leaders" replace />} />

                {/* ========================================
                    SCANNER ROUTES (Scanner Role Only)
                   ======================================== */}
                <Route path="/scanner" element={
                  <ScannerRoute>
                    <Screen_ScannerHome />
                  </ScannerRoute>
                } />
                <Route path="/scanner/scan" element={
                  <ScannerRoute>
                    <Screen_ScannerScan />
                  </ScannerRoute>
                } />
                <Route path="/scanner/settings" element={
                  <ScannerRoute>
                    <Screen_ScannerSettings />
                  </ScannerRoute>
                } />

                {/* ========================================
                    CATCH-ALL: 404 Redirect to Home
                   ======================================== */}
                <Route path="*" element={<NotFoundScreen />} />
          {/* MAIN_ROUTES_END */}
        </Routes>
      </Suspense>

      {/* OVERLAY ROUTES — rendered ON TOP of the background when a modal-style
          route (currently: event detail) was opened from another screen. Main
          <Routes> above keeps rendering the background screen, so going back
          is instant. */}
      {backgroundLocation && (
        <Suspense fallback={null}>
          <Routes>
            <Route path="/events/:eventId" element={
              <VisitorRoute>
                <EventDetailScreen />
              </VisitorRoute>
            } />
          </Routes>
        </Suspense>
      )}
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <DataCacheProvider>
        <AppModeProvider>
          <YouTubeProvider>
            <EventsProvider>
                <BrowserRouter>
                  <ScrollToTop />
                  <SWNavigationBridge />
                  <MemberPrefetcher />
                  <AuthenticatedOverlay />
                  <AppRoutes />
                  <Toaster />
            </BrowserRouter>
        </EventsProvider>
      </YouTubeProvider>
    </AppModeProvider>
  </DataCacheProvider>
  </AuthProvider>
</ThemeProvider>
  );
}

export default App;
